import { useContext, useEffect, useState } from 'react';
import { AuthContext } from 'contexts/AuthContext';
import { BallTriangle } from 'react-loader-spinner';
import toast from 'react-hot-toast';
import testService from 'ApiService/Teacher/TestService';
import DateField from 'components/fields/dateField';
import InputField from 'components/fields/InputField';
import Nodatafound from 'pages/Common/Nodatafound';
import Checkbox from 'components/checkbox';

const SelectTest = ({
  testData,
  selectedTest,
  setSelectedBatches,
  setSelectedTest,
  selectedBatches,
  setscheduledAt,
  scheduledAt,
  startTime,
  setStartTime,
  getStudents,
  attendance,
  setSelectedStudent,
  selectedStudent,
}) => {
  const [tests, setTests] = useState([]);
  const [batches, setBatches] = useState([]);
  const { state } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('mcq');
  const [selectedStudents, setSelectedStudents] = useState(new Set());

  useEffect(() => {
    if (testData) {
      setLoading(true);
      getStudents().finally(() => setLoading(false));
    }
  }, [testData]);

  const fetchTests = async () => {
    if (!testData.structureId) {
      setTests([]);
      return;
    }
    setLoading(true);
    try {
      const response = await testService.testByBatchStructure(testData.structureId);
      if (response.data.success) {
        setTests(response.data.data);
      } else {
        toast.error(response.data.message || 'Failed to fetch tests.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch tests.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    if (!testData.structureId) {
      setBatches([]);
      return;
    }
    try {
      const response = await testService.getBatchesByCourse(testData.courseId);
      if (response.data.success) {
        setBatches(response.data.batches);
      } else {
        toast.error(response.data.message || 'Failed to fetch batches.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch batches.');
    }
  };

  useEffect(() => {
    if (state?.user && testData.structureId) {
      fetchTests();
      fetchBatches();
    }
  }, [state?.user, testData.structureId]);

  const handleCheckboxChange = (studentId) => {
    const newSelectedStudents = new Set(selectedStudents);
    if (newSelectedStudents.has(studentId)) {
      newSelectedStudents.delete(studentId);
    } else {
      newSelectedStudents.add(studentId);
    }
    setSelectedStudents(newSelectedStudents);
    setSelectedStudent(Array.from(newSelectedStudents));
  };

  const handleBatchChange = (selectedBatchIds) => {
    setSelectedBatches(selectedBatchIds);
  };

  const handleTestSelection = (test) => {
    setSelectedTest(test);
  };

  const handleDateChange = (e) => {
    setscheduledAt(e.target.value);
  };

  const handleStartTimeChange = (e) => {
    setStartTime(e.target.value);
  };

  const filteredTests = tests.filter((t) => t.type === filterType);

  if (!testData.structureId) {
    return <Nodatafound title="Please select a batch structure first" />;
  }

  return (
    <div className="mt-4 h-full w-full rounded-[20px] p-4 md:mt-5">
      <div>
        <DateField
          extra="mb-3"
          label="Schedule At"
          id="scheduledAt"
          value={scheduledAt}
          onChange={handleDateChange}
        />
      </div>

      <div className="mb-4">
        <InputField
          label="Start Time"
          type="time"
          id="startTime"
          value={startTime}
          onChange={handleStartTimeChange}
          className="w-full rounded-md border p-2 dark:bg-navy-800"
        />
      </div>

      <div className="mb-4 flex justify-start">
        <button
          className={`mr-2 rounded-lg px-4 py-2 ${
            filterType === 'mcq'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 dark:text-navy-800'
          }`}
          onClick={() => setFilterType('mcq')}
        >
          MCQ
        </button>
        <button
          className={`rounded-lg px-4 py-2 ${
            filterType === 'qa'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 dark:text-navy-800'
          }`}
          onClick={() => setFilterType('qa')}
        >
          QA
        </button>
      </div>

      <div className="mb-6">
        <h5 className="mb-4 font-semibold text-navy-700 dark:text-white">
          Select Students
        </h5>
        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <BallTriangle color="#5431ed" />
          </div>
        ) : attendance.length === 0 ? (
          <Nodatafound title="No students available" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-white/10">
                  <th className="px-4 py-3 text-left text-xs font-bold">
                    SR.NO
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold">
                    SELECT
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold">
                    NAME
                  </th>
                </tr>
              </thead>
              <tbody>
                {attendance.map((student, index) => (
                  <tr
                    key={student._id}
                    className="border-b border-gray-200 dark:border-white/10"
                  >
                    <td className="px-4 py-3 text-sm">{index + 1}</td>
                    <td className="px-4 py-3">
                      <Checkbox
                        onChange={() => handleCheckboxChange(student._id)}
                        checked={selectedStudents.has(student._id)}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">{student.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <BallTriangle color="#5431ed" />
        </div>
      ) : filteredTests.length === 0 ? (
        <div className="flex h-40 items-center justify-center dark:text-gray-600">
          <Nodatafound
            title={`No ${filterType.toUpperCase()} tests found for this batch structure`}
          />
        </div>
      ) : (
        <div className="grid max-h-[68vh] w-full grid-cols-1 gap-4 overflow-hidden overflow-y-scroll rounded-xl border border-gray-200 p-2 dark:border-white/10 md:grid-cols-2 lg:grid-cols-3">
          {filteredTests.map((test, i) => (
            <div
              key={test._id}
              onClick={() => handleTestSelection(test._id)}
              className={`relative flex h-full cursor-pointer flex-col items-center rounded-lg border border-gray-200 p-4 dark:border-gray-700 md:flex-row ${
                selectedTest === test._id ? 'bg-brand-500' : ''
              }`}
            >
              <div className="flex h-full w-full flex-col leading-normal md:w-2/3">
                <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                  {i + 1}. {test.title}
                </h5>
                <p className="mb-3 font-normal text-navy-800 dark:text-white">
                  Total Questions: {test.questions.length}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Created by: {test.createdBy?.name || 'Unknown'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectTest;
